import * as React from 'react';
import { useHangarsState } from '../../containers/HangarsStateContainer';
import { MultiHangarStateContainer } from '../../containers/MultiHangarContainer';
import { Loading } from '../../widgets/Loading';
import { OpsPresenter } from './OpsPresenter';

/**
 * OpsView container - manages state for the unified queue + hangar view
 *
 * Creates MultiHangarStateContainer with the selected hangar IDs.
 * Uses a key to remount the container when selection changes, triggering data refetch.
 */
export const OpsView: React.FC = () => {
  const { hangars: availableHangars, ready: hangarsReady } = useHangarsState();

  // Store selected hangar IDs in localStorage for persistence
  const [selectedHangarIds, setSelectedHangarIds] = React.useState<string[]>(() => {
    const stored = localStorage.getItem('ops-selected-hangars');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  });

  // State for hangar picker dialog
  const [pickerOpen, setPickerOpen] = React.useState(false);

  // Persist selected hangars to localStorage
  React.useEffect(() => {
    localStorage.setItem('ops-selected-hangars', JSON.stringify(selectedHangarIds));
  }, [selectedHangarIds]);

  // Handle selecting hangars from picker
  const handleSelectHangars = React.useCallback((hangarIds: string[]) => {
    setSelectedHangarIds(hangarIds);
    setPickerOpen(false);
  }, []);

  // Open picker if no hangars selected and we have available hangars
  React.useEffect(() => {
    if (hangarsReady && availableHangars.length > 0 && selectedHangarIds.length === 0) {
      setPickerOpen(true);
    }
  }, [hangarsReady, availableHangars.length, selectedHangarIds.length]);

  if (!hangarsReady) {
    return <Loading />;
  }

  // Create a stable key from selected hangar IDs for remounting container
  const containerKey = selectedHangarIds.length > 0 ? selectedHangarIds.sort().join(',') : 'empty';

  return (
    <MultiHangarStateContainer
      key={containerKey}
      initialState={{
        hangarIds: selectedHangarIds,
        activeHangarId: selectedHangarIds[0] ?? null,
      }}
    >
      <OpsPresenter
        selectedHangarIds={selectedHangarIds}
        availableHangars={availableHangars}
        pickerOpen={pickerOpen}
        onOpenPicker={() => setPickerOpen(true)}
        onClosePicker={() => setPickerOpen(false)}
        onSelectHangars={handleSelectHangars}
      />
    </MultiHangarStateContainer>
  );
};
